// The guest's ticket — TICKETING-SPEC.md §5.3.
//
// Reached from the receipt and the receipt SMS as /t/<code>. The code IS the
// credential (≈40 bits, §5.1), so there is no session and no login: a guest who
// has the link has the ticket, which is exactly how a paper ticket works too.
//
// The QR is rendered HERE rather than embedded in the receipt e-mail, because
// mail clients strip data: URI images — an inline QR would be an empty box in
// Gmail for precisely the guests who need it at the door. The encoder is the
// dependency-free one the staff tool already ships (pages/Staff/qrcode.js via
// QrCanvas); html5-qrcode in package.json is a camera scanner, not a renderer.
//
// The payload is the bare CODE, not this page's URL: the inline encoder tops out
// around 230 bytes and a short code scans faster and from further away than a
// URL does. The staff scanner accepts either.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import QrCanvas from '../Staff/QrCanvas';
import { renderInfoText } from '../../utils/ticketInfoText';
import config from '../../config.json';
import './styles.css';

const API = (config && config.SERVER_URL) || 'http://localhost:5001/api';

const STATUS = {
  issued: { label: 'Gyldig billet', tone: 'ok' },
  used: { label: 'Allerede brugt', tone: 'spent' },
  cancelled: { label: 'Annulleret', tone: 'dead' },
  refunded: { label: 'Refunderet', tone: 'dead' },
};

const formatWhen = (event) => {
  if (!event) return '';
  if (event.date && event.start) {
    const d = new Date(`${event.date}T00:00:00`);
    if (!isNaN(d.getTime())) {
      return `${d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })} kl. ${event.start}`;
    }
  }
  return [event.date, event.start].filter(Boolean).join(' ');
};

export default function Ticket() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/tickets/t/${encodeURIComponent(String(code || '').toUpperCase())}`);
        if (alive) setTicket(res.data);
      } catch (err) {
        if (alive) {
          setError(
            err && err.response && err.response.status === 404
              ? 'Billetten findes ikke. Tjek linket i din kvittering.'
              : 'Billetten kunne ikke hentes. Prøv igen om lidt.',
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]);

  if (loading) {
    return <div className="tkt-page"><div className="tkt-card tkt-center">Henter billet…</div></div>;
  }

  if (error || !ticket) {
    return (
      <div className="tkt-page">
        <div className="tkt-card tkt-center">
          <div className="tkt-icon">🎫</div>
          <p>{error || 'Billetten findes ikke.'}</p>
        </div>
      </div>
    );
  }

  // A cancelled EVENT voids a still-valid ticket, whatever the ticket says.
  const state = ticket.event && ticket.event.cancelled && ticket.status === 'issued'
    ? { label: 'Arrangementet er aflyst', tone: 'dead' }
    : STATUS[ticket.status] || { label: ticket.status, tone: 'dead' };
  const event = ticket.event || {};

  return (
    <div className="tkt-page">
      <div className={`tkt-card tkt-${state.tone}`}>
        <div className={`tkt-status tkt-status-${state.tone}`}>{state.label}</div>

        {event.image ? <img className="tkt-image" src={event.image} alt="" /> : null}

        <h1 className="tkt-title">{event.name}</h1>
        <div className="tkt-when">{formatWhen(event)}</div>
        {event.location || ticket.restaurant ? (
          <div className="tkt-where">{event.location || ticket.restaurant}</div>
        ) : null}

        {/* Greyed out rather than hidden once the ticket is spent: the guest
            still needs to see WHICH ticket this was, and a blank card at a door
            helps nobody. */}
        <div className={`tkt-qr${ticket.status === 'issued' && !(event && event.cancelled) ? '' : ' tkt-qr-spent'}`}>
          <QrCanvas value={ticket.code} size={232} />
        </div>

        <div className="tkt-code">{ticket.code}</div>
        <div className="tkt-type">
          {ticket.type}
          {ticket.buyerName ? ` · ${ticket.buyerName}` : ''}
        </div>

        {/* The venue's practical information, read live from the event — a
            guest checking their ticket on the night should see tonight's door
            time, not the one that was set when they bought it. */}
        {event.infoText ? (
          <div className="tkt-info">{renderInfoText(event.infoText)}</div>
        ) : null}

        {ticket.status === 'issued' ? (
          <p className="tkt-hint">Vis denne kode ved indgangen.</p>
        ) : ticket.status === 'used' ? (
          <p className="tkt-hint">
            Billetten blev scannet
            {ticket.usedAt ? ` ${new Date(ticket.usedAt).toLocaleString('da-DK')}` : ''}.
          </p>
        ) : (
          <p className="tkt-hint">Billetten er ikke længere gyldig.</p>
        )}
      </div>
    </div>
  );
}
